import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { axiosPrivate } from "../api/axios";
import useAuth from "./useAuth";
import useRefreshToken from "./useRefreshToken";
import PageRoutes from "../utils/routes";

const useAxiosPrivate = () => {
  const navigate = useNavigate();
  const { firmAuth, personnelAuth } = useAuth();
  const refresh = useRefreshToken();

  useEffect(() => {
    // First, let's create our request interceptor to add the token to outgoing requests
    const requestInterceptor = axiosPrivate.interceptors.request.use(
      (config) => {
        // We'll determine which access token to use based on which auth state is active
        const accessToken = firmAuth?.accessToken || personnelAuth?.accessToken;

        // If we don't already have an Authorization header and we have a token,
        // let's add it to our request
        if (!config.headers["Authorization"] && accessToken) {
          config.headers["Authorization"] = `Bearer ${accessToken}`;
        }

        // Handle multipart form data properly
        if (config.multiPart) {
          config.headers["Content-Type"] = "multipart/form-data";
        } else {
          config.headers["Content-Type"] = "application/json";
        }

        return config;
      },
      (error) => {
        // If there's an error in our request configuration, reject the promise
        return Promise.reject(error);
      }
    );

    // Now, let's create our response interceptor to handle token expiration
    const responseInterceptor = axiosPrivate.interceptors.response.use(
      // If the response is successful, just return it
      (response) => response,
      // If there's an error, we need to handle it
      async (error) => {
        // Get the original request that failed
        const prevRequest = error?.config;

        // Check if the error was due to an expired token
        //static const tokenExpired = 440;
        //static const tokenInvalid = 443;

        // and ensure we haven't already tried to refresh the token for this request
        if (
          (error?.response?.status === 440 ||
            error?.response?.status === 443) &&
          !prevRequest?.sent
        ) {
          // Mark this request as having been retried
          prevRequest.sent = true;

          try {
            // Attempt to get a new access token
            const newAccessToken = await refresh();

            // If we successfully got a new token, retry the original request
            if (newAccessToken) {
              prevRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
              return axiosPrivate(prevRequest);
            } else {
              // If we didn't get a new token, we need to log the user out
              throw new Error("Failed to refresh token");
            }
          } catch (refreshError) {
            // If refresh failed, we need to redirect to the appropriate login page
            console.error("Token refresh failed:", refreshError);

            // Determine which login page to use based on the last active auth
            const loginPath = PageRoutes.authHomePage;

            // Navigate to login and clear auth state
            navigate(loginPath, { replace: true });

            // Reject the promise to prevent the original request from continuing
            return Promise.reject(refreshError);
          }
        }

        // If the error wasn't a 401 or we've already tried refreshing,
        // just reject the promise
        return Promise.reject(error);
      }
    );

    // Clean up function to remove the interceptors when the component unmounts
    // This prevents memory leaks and duplicate interceptors
    return () => {
      axiosPrivate.interceptors.request.eject(requestInterceptor);
      axiosPrivate.interceptors.response.eject(responseInterceptor);
    };
  }, [firmAuth, personnelAuth, refresh, navigate]);

  // Return the axios instance with our interceptors attached
  return axiosPrivate;
};

export default useAxiosPrivate;
